
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Team {
  id: string;
  name: string;
  logo?: string;
  photo?: string;
}

interface AddGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGameAdded: () => void;
}

const AddGameModal: React.FC<AddGameModalProps> = ({ isOpen, onClose, onGameAdded }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [formData, setFormData] = useState({
    home_team: '',
    away_team: '',
    home_team_logo: '',
    away_team_logo: '',
    date: '',
    time: '',
    location: '',
    jornada: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchAllTeams();
    }
  }, [isOpen]);

  const fetchAllTeams = async () => {
    try {
      // Cargar equipos de la tabla teams
      const { data: teamsData, error: teamsError } = await supabase
        .from('teams')
        .select('id, name, logo')
        .order('name', { ascending: true });

      if (teamsError) throw teamsError;

      // Cargar equipos de la tabla opponent_teams
      const { data: opponentTeamsData, error: opponentError } = await supabase
        .from('opponent_teams')
        .select('id, name, logo, logo_img')
        .order('name', { ascending: true });

      if (opponentError) throw opponentError;

      // Combinar ambas listas
      const allTeams: Team[] = [
        ...(teamsData || []).map(t => ({ 
          id: t.id, 
          name: t.name, 
          photo: t.logo 
        })),
        ...(opponentTeamsData || []).map(t => ({ 
          id: t.id, 
          name: t.name, 
          // Priorizar logo_img sobre logo
          photo: t.logo_img || t.logo 
        }))
      ];

      setTeams(allTeams);
    } catch (error) {
      console.error('Error al cargar equipos:', error);
    }
  };

  const handleTeamSelect = (field: 'home_team' | 'away_team', teamName: string) => {
    const selectedTeam = teams.find(t => t.name === teamName);
    
    // Priorizar logo_img (imagen subida) sobre logo (URL externa)
    let logoUrl = '';
    if (selectedTeam?.photo) {
      logoUrl = selectedTeam.photo;
    } else {
      // Generar URL del bucket basada en el nombre del equipo
      const teamSlug = teamName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      logoUrl = `https://yfsgqrzophdjgqardzvy.supabase.co/storage/v1/object/public/media/${teamSlug}.png`;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: teamName,
      [`${field}_logo`]: logoUrl
    }));
  };

  const openMediaSelector = (field: 'home_team_logo' | 'away_team_logo') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }

        if (!file.type.startsWith('image/')) {
          alert('Por favor selecciona un archivo de imagen válido.');
          return;
        }

        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setFormData(prev => ({ ...prev, [field]: result }));
          };
          reader.onerror = () => {
            alert('Error al leer el archivo. Por favor intenta de nuevo.');
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error al procesar imagen:', error);
          alert('Error al procesar la imagen');
        }
      }
    };
    input.click();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const gameData = {
        home_team: formData.home_team,
        away_team: formData.away_team,
        home_team_logo: formData.home_team_logo || null,
        away_team_logo: formData.away_team_logo || null,
        date: formData.date,
        time: formData.time,
        location: formData.location || '',
        jornada: formData.jornada ? parseInt(formData.jornada) : null,
        title: (formData as any).title || null,
      };

      console.log('Datos a insertar:', gameData);

      const { data, error } = await supabase
        .from('games')
        .insert([gameData])
        .select();

      if (error) {
        console.error('Error de Supabase:', error);
        throw new Error(`Error de base de datos: ${error.message}`);
      }

      console.log('Partido añadido exitosamente:', data);
      setFormData({
        home_team: '',
        away_team: '',
        home_team_logo: '',
        away_team_logo: '',
        date: '',
        time: '',
        location: '',
        jornada: ''
      });

      onGameAdded();
      onClose();
    } catch (error) {
      console.error('Error al añadir partido:', error);
      alert('Error al añadir el partido. Por favor intenta de nuevo.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Añadir Nuevo Partido</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            disabled={isLoading}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded text-red-200 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Jornada *
            </label>
            <select
              name="jornada"
              value={formData.jornada}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              required
              disabled={isLoading}
            >
              <option value="">Seleccionar jornada</option>
              {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>
                  Jornada {num}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Equipo Local *
            </label>
            <select
              value={formData.home_team}
              onChange={(e) => handleTeamSelect('home_team', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              required
              disabled={isLoading}
            >
              <option value="">Seleccionar equipo</option>
              {teams.map((team) => (
                <option key={`home-${team.id}`} value={team.name}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Logo Equipo Local
            </label>
            <button
              type="button"
              onClick={() => openMediaSelector('home_team_logo')}
              className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 border-2 border-dashed border-gray-600 transition-colors"
              disabled={isLoading}
            >
              <i className="ri-upload-line mr-2"></i>
              {formData.home_team_logo ? 'Cambiar Logo' : 'Seleccionar desde Medios'}
            </button>
            {formData.home_team_logo && (
              <div className="mt-2">
                <img
                  src={formData.home_team_logo}
                  alt="Preview"
                  className="w-16 h-16 object-contain mx-auto border border-gray-600 rounded"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Equipo Visitante *
            </label>
            <select
              value={formData.away_team}
              onChange={(e) => handleTeamSelect('away_team', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              required
              disabled={isLoading}
            >
              <option value="">Seleccionar equipo</option>
              {teams.map((team) => (
                <option key={`away-${team.id}`} value={team.name}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Logo Equipo Visitante
            </label>
            <button
              type="button"
              onClick={() => openMediaSelector('away_team_logo')}
              className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 border-2 border-dashed border-gray-600 transition-colors"
              disabled={isLoading}
            >
              <i className="ri-upload-line mr-2"></i>
              {formData.away_team_logo ? 'Cambiar Logo' : 'Seleccionar desde Medios'}
            </button>
            {formData.away_team_logo && (
              <div className="mt-2">
                <img
                  src={formData.away_team_logo}
                  alt="Preview"
                  className="w-16 h-16 object-contain mx-auto border border-gray-600 rounded"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Fecha *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Hora *
            </label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Ubicación
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
              disabled={isLoading}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Añadiendo...
                </div>
              ) : (
                'Añadir Partido'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGameModal;
